import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Card, Col, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getSystems } from '../api/api';
import { Isystem } from '../types/news';
import Slider from 'react-slick';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition, IconName, findIconDefinition, library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';

library.add(fas);

const bounceAnimation = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-30px);
  }
  60% {
    transform: translateY(-15px);
  }
`;

const BackgroundImage = styled.img`
  width: 100%;
  height: 24rem;
  object-fit: cover;
  filter: blur(4px);
`;

const Container = styled.div`
  position: relative;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const Title = styled.h1`
  color: #ffffff;
  font-size: 2.25rem;
  margin-top: 2.5rem;
`;

const SliderContainer = styled.div`
  width: 80%;
`;

const IconContainer = styled.div`
  background-color: #002856;
  border-radius: 9999px;
  width: 10rem;
  height: 10rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  transition: transform 0.3s ease;
  cursor: pointer;
  &:hover {
    transform: scale(1.1);
  }
  &:hover .fa-icon {
    animation: ${bounceAnimation} 1.5s;
  }
`;

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
  color: #ffffff;
  font-size: 3rem;
`;

const AppTitle = styled.p`
  color: #ffffff;
  margin-top: 0.5rem;
  text-align: center;
`;

const AppItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
`;

const SLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: block;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

interface IconProps {
  iconName: string;
}

const Icon: React.FC<IconProps> = ({ iconName }) => {
  const formattedIconName = iconName as IconName;
  const iconDefinition: IconDefinition | null = findIconDefinition({ prefix: 'fas', iconName: formattedIconName });

  return iconDefinition ? <StyledFontAwesomeIcon icon={iconDefinition} className="fa-icon" /> : null;
};

const ApplicationsShowcase = ({ limit = 500 }) => {
  const { t, i18n } = useTranslation();
  const { isLoading, error, data } = useQuery('SystemsData', () =>
    getSystems({ limit, language: i18n.language })
  );

  useEffect(() => {
    // Refetching whenever the language changes
  }, [i18n.language]);

  if (isLoading) return <div>{t('misc.loading')}</div>;
  if (error) return <div>{t('applications_showcase.error_loading')}</div>;

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 4,
    rtl: i18n.language === 'ar',
    autoplay: false,
    arrows: true,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
        },
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  return (
    <Container>
      <BackgroundImage
        src="https://images.pexels.com/photos/4186017/pexels-photo-4186017.jpeg"
        alt="Background"
      />
      <Overlay>
        <Title>{t('applications_showcase.header')}</Title>
        <SliderContainer>
          <Slider {...settings}>
            {data?.data.map((item: Isystem) => (
              <AppItem key={item.id}>
                <SLink to={`${item.attributes.url}`} target="_blank">
                  <IconContainer>
                    <Icon iconName={item.attributes.icon} />
                  </IconContainer>
                  <AppTitle>{item.attributes.Title}</AppTitle>
                </SLink>
              </AppItem>
            ))}
          </Slider>
        </SliderContainer>
      </Overlay>
    </Container>
  );
};

export default ApplicationsShowcase;
